import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import Reveal from "@/components/common/Reveal";
import ProjectPlaceholder from "@/components/common/ProjectPlaceholder";

export default function Portfolio() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const categories = useMemo(() => {
    const set = new Set(projects.map((p) => p.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [projects]);

  const filtered = useMemo(
    () => (filter === "All" ? projects : projects.filter((p) => p.category === filter)),
    [filter, projects]
  );

  return (
    <>
      <section className="pt-20 md:pt-24 pb-6 md:pb-10 max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14" data-testid="portfolio-hero">
        <div className="mono-label text-zinc-500">Portfolio</div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] font-light mt-3 max-w-4xl">
          Selected <em className="not-italic text-[#BE1E2D] font-normal">Works.</em>
        </h1>
      </section>

      {/* FILTERS */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 pb-6" data-testid="portfolio-filters">
        <div className="flex flex-wrap gap-2 border-t border-b border-zinc-200 py-4">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-4 py-1.5 text-[10px] uppercase tracking-[0.16em] font-medium transition-colors border ${
                filter === c ? "bg-black text-white border-black" : "bg-white text-black border-zinc-300 hover:border-black"
              }`}
              data-testid={`portfolio-filter-${c.toLowerCase()}`}
            >
              {c}
            </button>
          ))}
          <div className="ml-auto mono-label text-zinc-500 self-center">
            {filtered.length} {filtered.length === 1 ? "project" : "projects"}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 pb-16 md:pb-24">
        {loading ? (
          <div className="py-24 text-center mono-label text-zinc-500">Loading projects…</div>
        ) : filtered.length === 0 ? (
          <div className="py-24 text-center mono-label text-zinc-500">No projects in this category.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 lg:gap-8">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.08}>
                <Link to={`/portfolio/${p.slug}`} className="block group" data-testid={`portfolio-card-${p.slug}`}>
                  <div className="img-hover-scale aspect-[4/5] bg-zinc-100">
                    {p.cover_image ? (
                      <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <ProjectPlaceholder title={p.title} category={p.category} className="w-full h-full" />
                    )}
                  </div>
                  <div className="mt-3 md:mt-4 flex items-start justify-between gap-3">
                    <div>
                      <div className="mono-label text-zinc-500">
                        {p.category} · {p.year}
                      </div>
                      <h3 className="font-display text-base md:text-lg lg:text-xl mt-1.5 md:mt-2 leading-tight group-hover:text-[#BE1E2D] transition-colors">
                        {p.title}
                      </h3>
                      <div className="text-xs md:text-sm text-zinc-600 mt-1">{p.client}</div>
                    </div>
                    <ArrowUpRight className="w-4 h-4 md:w-5 md:h-5 text-zinc-400 group-hover:text-black group-hover:-translate-y-1 group-hover:translate-x-1 transition-all mt-1 flex-shrink-0" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
